/**
 * Filling a Snapshot cache and recording the result (issue #3, ADR-0016): the first install,
 * a resumed install, and the background staging of an update. The page does the downloading
 * (the Cache API is the same one the worker serves from; a page outlives a worker's event
 * budget), verifying every file's SHA-256 from the manifest before it is put in the cache, so
 * a cache entry is always a whole, correct file and an interrupted download resumes with the
 * missing files. `Env` is the browser surface, replaced in tests.
 */
import type { Manifest } from "../../shared/snapshot-format";
import { SNAPSHOT_FORMAT } from "../../shared/snapshot-format";
import type { Installed, PwaState } from "./state";
import { dataCacheName, isDataCache, readState, shellCacheName, writeState } from "./state";
import type { DataFile, Deployed, DownloadProgress } from "./update";
import { advanceProgress, compareDeployed, dataUrl, downloadUrl, initialProgress, manifestHash, planDownload, sha256Hex, snapshotFiles } from "./update";

export interface Env {
  caches: CacheStorage;
  fetch: (url: string, init?: RequestInit) => Promise<Response>;
  now: () => Date;
  /** Files fetched at once. */
  concurrency?: number;
  /** Attempts per file before the download fails. */
  attempts?: number;
}

export interface NetworkManifest {
  text: string;
  manifest: Manifest;
  hash: string;
}

export class DownloadError extends Error {
  constructor(message: string, readonly path: string | null, readonly offline: boolean) {
    super(message);
  }
}

function fresh(url: string, now: Date): string {
  return `${url}?fresh=${now.getTime().toString(36)}`;
}

/** The manifest the network serves now, bypassing the worker and any HTTP cache. */
export async function fetchNetworkManifest(env: Env): Promise<NetworkManifest> {
  let res: Response;
  try {
    res = await env.fetch(fresh("/data/manifest.json", env.now()), { cache: "no-store" });
  } catch (e) {
    throw new DownloadError(`The Snapshot manifest could not be reached: ${(e as Error).message}`, "manifest.json", true);
  }
  if (!res.ok) throw new DownloadError(`${res.status} ${res.statusText} loading the Snapshot manifest`, "manifest.json", false);
  const text = await res.text();
  const manifest = JSON.parse(text) as Manifest;
  if (manifest.format !== SNAPSHOT_FORMAT) throw new DownloadError(`Snapshot format ${manifest.format} is not supported by this build (wants ${SNAPSHOT_FORMAT})`, "manifest.json", false);
  return { text, manifest, hash: await manifestHash(text) };
}

/** The build the network serves now (`version.json`, written beside the shell at build time). */
export async function fetchNetworkVersion(env: Env): Promise<{ build: string; version: string }> {
  let res: Response;
  try {
    res = await env.fetch(fresh("/version.json", env.now()), { cache: "no-store" });
  } catch (e) {
    throw new DownloadError(`The deployed version could not be reached: ${(e as Error).message}`, "version.json", true);
  }
  if (!res.ok) throw new DownloadError(`${res.status} ${res.statusText} loading version.json`, "version.json", false);
  const v = (await res.json()) as { build?: unknown; version?: unknown };
  if (typeof v.build !== "string") throw new DownloadError("version.json names no build", "version.json", false);
  return { build: v.build, version: typeof v.version === "string" ? v.version : "" };
}

/** The paths a Snapshot cache holds. */
export async function cachedPaths(cache: Cache): Promise<Set<string>> {
  const paths = new Set<string>();
  for (const req of await cache.keys()) {
    const { pathname } = new URL(req.url);
    if (pathname.startsWith("/data/")) paths.add(pathname.slice("/data/".length));
  }
  return paths;
}

/** The manifest a Snapshot cache was filled from, or null when the cache never got that far. */
export async function cachedManifest(cache: Cache): Promise<Manifest | null> {
  const res = await cache.match(dataUrl("manifest.json"));
  if (!res) return null;
  try {
    return (await res.json()) as Manifest;
  } catch {
    return null;
  }
}

function jsonResponse(body: BodyInit, bytes: number): Response {
  return new Response(body, { status: 200, headers: { "content-type": "application/json", "content-length": String(bytes) } });
}

async function fetchVerified(env: Env, file: DataFile): Promise<ArrayBuffer> {
  const attempts = env.attempts ?? 3;
  let last: Error | null = null;
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await env.fetch(downloadUrl(file), { cache: "no-store" });
      if (!res.ok) throw new DownloadError(`${res.status} ${res.statusText} loading ${file.path}`, file.path, false);
      const buf = await res.arrayBuffer();
      const hash = await sha256Hex(buf);
      if (hash !== file.sha256) throw new DownloadError(`${file.path} arrived corrupted (SHA-256 ${hash.slice(0, 12)}, wanted ${file.sha256.slice(0, 12)})`, file.path, false);
      return buf;
    } catch (e) {
      last = e instanceof DownloadError ? e : new DownloadError(`${file.path} could not be downloaded: ${(e as Error).message}`, file.path, true);
      if (i + 1 < attempts) await new Promise((r) => setTimeout(r, 250 * 2 ** i));
    }
  }
  throw last;
}

export interface DownloadOptions {
  onProgress?: (p: DownloadProgress) => void;
  signal?: AbortSignal;
}

/**
 * Fill the cache for `target` with every file its manifest lists: files already there stay,
 * files another Snapshot cache holds with the same hash are copied, the rest are fetched and
 * verified. Returns the plan it ran. Throws a DownloadError on the first file that fails
 * after its retries; what was cached before stays for the next attempt.
 */
export async function downloadSnapshot(env: Env, target: NetworkManifest, options: DownloadOptions = {}): Promise<DownloadProgress> {
  const name = dataCacheName(target.hash);
  const cache = await env.caches.open(name);
  if (!(await cache.match(dataUrl("manifest.json")))) {
    await cache.put(dataUrl("manifest.json"), jsonResponse(target.text, new TextEncoder().encode(target.text).byteLength));
  }
  const others: { cache: string; manifest: Manifest }[] = [];
  for (const other of await env.caches.keys()) {
    if (other === name || !isDataCache(other)) continue;
    const m = await cachedManifest(await env.caches.open(other));
    if (m) others.push({ cache: other, manifest: m });
  }
  const present = await cachedPaths(cache);
  const plan = planDownload(target.manifest, present, others);
  const files = snapshotFiles(target.manifest);
  let progress = initialProgress(files, present);
  const report = () => options.onProgress?.(progress);
  report();

  for (const { file, from } of plan.copy) {
    options.signal?.throwIfAborted();
    const src = await env.caches.open(from.cache);
    const res = await src.match(dataUrl(from.path));
    if (!res) {
      plan.fetch.push(file);
      continue;
    }
    await cache.put(dataUrl(file.path), res.clone());
    progress = advanceProgress(progress, file);
    report();
  }

  const queue = [...plan.fetch];
  const workers = Array.from({ length: Math.max(1, env.concurrency ?? 4) }, async () => {
    for (let file = queue.shift(); file; file = queue.shift()) {
      options.signal?.throwIfAborted();
      const buf = await fetchVerified(env, file);
      await cache.put(dataUrl(file.path), jsonResponse(buf, buf.byteLength));
      progress = advanceProgress(progress, file);
      report();
    }
  });
  await Promise.all(workers);

  const missing = files.filter((f) => !present.has(f.path)).map((f) => f.path);
  const after = await cachedPaths(cache);
  const lost = missing.filter((p) => !after.has(p));
  if (lost.length > 0) throw new DownloadError(`${lost.length} files did not reach the cache (${lost[0]})`, lost[0]!, false);
  return progress;
}

function installedFrom(build: string, target: NetworkManifest, at: Date): Installed {
  return {
    build,
    data: target.hash,
    release: target.manifest.snapshot.release,
    totalBytes: target.manifest.totalBytes,
    files: snapshotFiles(target.manifest).length + 1,
    at: at.toISOString(),
  };
}

/**
 * The first install (or its resumption): fetch the manifest the network serves, fill its
 * cache, record it as active under `build`. Everything before the record is idempotent, so a
 * launch after an interrupted download picks up the missing files.
 */
export async function installSnapshot(env: Env, build: string, options: DownloadOptions = {}): Promise<Installed> {
  const target = await fetchNetworkManifest(env);
  const before = await readState(env.caches);
  if (before.active) return before.active;
  if (before.installing !== target.hash) await writeState(env.caches, { ...before, installing: target.hash });
  await downloadSnapshot(env, target, options);
  const state = await readState(env.caches);
  const active = installedFrom(build, target, env.now());
  await writeState(env.caches, { ...state, active, installing: null });
  for (const other of await env.caches.keys()) {
    if (isDataCache(other) && other !== dataCacheName(target.hash)) await env.caches.delete(other);
  }
  return active;
}

export type CheckOutcome =
  | { kind: "current"; active: Installed }
  /** A staged update, complete and waiting for a launch. */
  | { kind: "ready"; staged: Installed }
  /** The Snapshot is staged but the new shell has not finished caching; the next check tries again. */
  | { kind: "shell-pending"; deployed: Deployed & { release: string } }
  | { kind: "not-installed" };

export interface ShellHooks {
  /** Ask the browser for a new service worker and wait until it is installed (its shell cached). */
  update: () => Promise<void>;
  /** Whether the shell cache for `build` holds the index page. */
  ready: (build: string) => Promise<boolean>;
}

/** Whether a shell cache holds its index page (the worker's install completed). */
export async function shellCached(storage: CacheStorage, build: string): Promise<boolean> {
  if (!(await storage.has(shellCacheName(build)))) return false;
  const cache = await storage.open(shellCacheName(build));
  return (await cache.match("/")) !== undefined;
}

/**
 * The background check: compare what the network serves with what is active and stage the
 * difference. New Snapshot files download into a cache of their own while the current launch
 * keeps reading the active one; a new shell is cached by its own worker's install. Only when
 * both are complete is the pair recorded as staged. Throws a DownloadError when the network
 * is unreachable (the caller treats it as "checked, offline").
 */
export async function checkForUpdate(env: Env, shell: ShellHooks, options: DownloadOptions = {}): Promise<CheckOutcome> {
  const state = await readState(env.caches);
  if (!state.active) return { kind: "not-installed" };
  const [version, target] = await Promise.all([fetchNetworkVersion(env), fetchNetworkManifest(env)]);
  const deployed: Deployed = { build: version.build, data: target.hash };
  const checked = env.now().toISOString();
  const difference = compareDeployed(state.active, deployed);
  if (difference === "same") {
    // A deploy went back to what is active: whatever was staged is moot.
    await writeState(env.caches, { ...state, staged: null, installing: null, lastCheck: checked });
    return { kind: "current", active: state.active };
  }
  if (state.staged && state.staged.build === deployed.build && state.staged.data === deployed.data) {
    await writeState(env.caches, { ...state, lastCheck: checked });
    return { kind: "ready", staged: state.staged };
  }
  if (difference !== "shell") {
    if (state.installing !== target.hash) await writeState(env.caches, { ...state, staged: null, installing: target.hash, lastCheck: checked });
    await downloadSnapshot(env, target, options);
  }
  if (!(await shell.ready(deployed.build))) {
    await shell.update();
    if (!(await shell.ready(deployed.build))) {
      const s = await readState(env.caches);
      await writeState(env.caches, { ...s, lastCheck: checked });
      return { kind: "shell-pending", deployed: { ...deployed, release: target.manifest.snapshot.release } };
    }
  }
  const latest = await readState(env.caches);
  const staged = installedFrom(deployed.build, target, env.now());
  await writeState(env.caches, { ...latest, staged, installing: null, lastCheck: checked });
  return { kind: "ready", staged };
}

export interface Applied {
  from: Installed;
  to: Installed;
}

/**
 * Apply the staged pair: read the old Snapshot's index for the migration report, flip the
 * state in one write (the worker serves the new shell and Snapshot from here on), run
 * `migrate` with that index, then drop the old caches. The caller reloads afterwards.
 */
export async function applyStaged(env: Env, migrate: (previousIndex: unknown) => Promise<void>, afterFlip?: () => Promise<void>): Promise<Applied | null> {
  const state = await readState(env.caches);
  if (!state.active || !state.staged) return null;
  const from = state.active;
  const to = state.staged;
  let previousIndex: unknown = null;
  try {
    const old = await env.caches.open(dataCacheName(from.data));
    const m = await cachedManifest(old);
    const res = m ? await old.match(dataUrl(m.index.path)) : undefined;
    previousIndex = res ? await res.json() : null;
  } catch {
    previousIndex = null;
  }
  await writeState(env.caches, { ...state, active: to, staged: null, installing: null });
  await afterFlip?.();
  await migrate(previousIndex);
  if (from.data !== to.data) await env.caches.delete(dataCacheName(from.data));
  if (from.build !== to.build) await env.caches.delete(shellCacheName(from.build));
  return { from, to };
}

/** Whether a Snapshot cache holds every file its manifest lists. */
export async function snapshotComplete(storage: CacheStorage, hash: string): Promise<boolean> {
  if (!(await storage.has(dataCacheName(hash)))) return false;
  const cache = await storage.open(dataCacheName(hash));
  const m = await cachedManifest(cache);
  if (!m) return false;
  const present = await cachedPaths(cache);
  return snapshotFiles(m).every((f) => present.has(f.path));
}

export type { PwaState };
